import type { ParseWorkflowResult } from '@/app-shell/rpa/recorder/lib/parse-workflow';
import type {
  WorkflowDetailErrorKind,
  WorkflowDetailPresentation,
} from '@/app-shell/rpa/recorder/types/workflow-detail';

export interface WorkflowDetailPresentationSignals {
  readonly bridgeAvailable: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** `null` while workflow payload has not been fetched yet. */
  readonly parseResult: ParseWorkflowResult | null;
  readonly recordingName?: string;
  readonly untitledFallback: string;
}

export function deriveWorkflowDetailPresentation(
  signals: WorkflowDetailPresentationSignals
): WorkflowDetailPresentation {
  const rawWorkflowName = signals.parseResult?.ok
    ? signals.parseResult.detail.name
    : undefined;
  // Blank names are treated as missing so untitledFallback can surface.
  const workflowName = rawWorkflowName?.trim() ? rawWorkflowName.trim() : undefined;
  const title = signals.recordingName ?? workflowName ?? signals.untitledFallback;

  if (!signals.bridgeAvailable) {
    return { surfaceKind: 'unavailable', title, steps: [] };
  }

  if (signals.isLoading || signals.parseResult === null) {
    return { surfaceKind: 'loading', title, steps: [] };
  }

  if (signals.isError) {
    return {
      surfaceKind: 'error',
      title,
      steps: [],
      errorKind: 'fetch',
    };
  }

  if (!signals.parseResult.ok) {
    return {
      surfaceKind: 'error',
      title,
      steps: [],
      errorKind: parseFailureToErrorKind(signals.parseResult.reason),
    };
  }

  const detail = signals.parseResult.detail;

  if (detail.steps.length === 0) {
    return { surfaceKind: 'empty', title, steps: [] };
  }

  return {
    surfaceKind: 'ready',
    title,
    steps: detail.steps,
    ...(detail.description ? { description: detail.description } : {}),
    ...(detail.prerequisites?.length ? { prerequisites: detail.prerequisites } : {}),
    ...(detail.notes?.length ? { notes: detail.notes } : {}),
    ...(detail.parameters.length > 0 ? { parameters: detail.parameters } : {}),
  };
}

function parseFailureToErrorKind(
  reason: 'missing_payload' | 'invalid_payload'
): WorkflowDetailErrorKind {
  return reason;
}
